import { CheckCircle, Clock, Undo2, XCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import type { Payment, PaymentStatus } from "@/api/payments";

const STATUS_LABELS = {
  PENDING: "Pending",
  PROCESSING: "Processing",
  CREDIT_APPROVED: "Credit approved",
  PAID: "Paid",
  PARTIAL: "Partial",
  FAILED: "Failed",
  REFUNDED: "Refunded",
  PARTIAL_REFUND: "Partially refunded",
  VOIDED: "Voided",
} satisfies Record<PaymentStatus, string>;

type HistoryPayment = Pick<
  Payment,
  "status" | "transactionType" | "amountCents" | "method" | "createdAt"
>;

export function PaymentHistoryRow({ payment }: { payment: HistoryPayment }) {
  const isRefund = payment.transactionType === "CREDIT";
  const settled =
    payment.status === "PAID" ||
    payment.status === "REFUNDED" ||
    payment.status === "PARTIAL_REFUND";
  const failed = payment.status === "FAILED" || payment.status === "VOIDED";
  const label =
    payment.status === "PAID"
      ? isRefund
        ? "Refund"
        : "Payment"
      : STATUS_LABELS[payment.status];
  const amount = new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
  }).format(payment.amountCents / 100);

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 text-sm py-2 px-3 rounded bg-gray-50">
      <div className="flex items-center gap-3">
        {settled ? (
          isRefund ? (
            <Undo2 aria-hidden="true" className="h-4 w-4 text-red-500" />
          ) : (
            <CheckCircle
              aria-hidden="true"
              className="h-4 w-4 text-green-500"
            />
          )
        ) : failed ? (
          <XCircle aria-hidden="true" className="h-4 w-4 text-red-500" />
        ) : (
          <Clock aria-hidden="true" className="h-4 w-4 text-muted-foreground" />
        )}
        <div>
          <span className="font-medium">
            {settled ? (isRefund ? "-" : "+") : ""}
            {amount}
          </span>
          <span className="text-muted-foreground ml-2">{payment.method}</span>
          {!settled && (
            <div className="text-xs text-muted-foreground">
              {payment.status === "PARTIAL"
                ? "Check order balance"
                : "Receipt not confirmed"}
            </div>
          )}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-muted-foreground">
        <span>
          {new Date(payment.createdAt).toLocaleString(undefined, {
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
          })}
        </span>
        <Badge
          variant={
            failed || (settled && isRefund)
              ? "destructive"
              : settled
                ? "default"
                : "outline"
          }
        >
          {label}
        </Badge>
      </div>
    </div>
  );
}
